// MiniDrawer.jsx
import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import IconMask from "../IconMask";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { app } from "../../services";
import firebase from "../../services/firebase";

import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ListItemSecondaryAction,
  Paper,
  Popover,
  useMediaQuery,
} from "@mui/material";
import useMeasure from "react-use-measure";
import Login from "../Login";
import {
  removeGlobalUser,
  removeInstitution,
  removeToken,
  setInstitution,
} from "../../redux/session";
import ThemeButton from "../customForms/ThemeButton";
import CatalogsIconButton from "../customForms/CatalogsIconButton";
import { instituciones, usuarios } from "../../constants/models";
import { Helmet } from "react-helmet";
import "./MiniDrawer.css";
import { DeleteForeverOutlined } from "@mui/icons-material";
import NotificationComponent from "../../common/NotificationComponent";
import { Zoom } from "@material-ui/core";
import { TransitionUp } from "../../common/TransitionComponents";
import { socketApp } from "../../services";

const drawerWidth = 240;

/** =========================
 *  Mixins
 *  ========================= */
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 0,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

/** =========================
 *  Styled
 *  ========================= */
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

// AppBar transparente (si quieres glass, lo cambio)
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: "transparent",
  boxShadow: "none",
  border: "none",
  backdropFilter: "none",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// Drawer transparente (clave: .MuiDrawer-paper)
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",

  "& .MuiDrawer-paper": {
    background: "transparent !important",
    backgroundImage: "none !important",
    boxShadow: "none !important",
    border: "none !important",
    outline: "none !important",
    color: theme.palette.text.primary,
    overflowX: "hidden",
  },

  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      background: "transparent !important",
      backgroundImage: "none !important",
      boxShadow: "none !important",
      border: "none !important",
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      background: "transparent !important",
      backgroundImage: "none !important",
      boxShadow: "none !important",
      border: "none !important",
    },
  }),
}));

/** =========================
 *  Routes
 *  ========================= */
const routes = [
  { name: "Mis clientes", path: "/", icon: "assets/icons/dashboard.svg" },
  { name: "Clientes", path: "/clients", icon: "assets/icons/avatar.svg" },
  { name: "Mis productos", path: "/mis-productos", icon: "assets/icons/ahorro.svg", clientOnly: true },
  { name: "Inicio", path: "/home", icon: "assets/icons/connect.svg" },
  { name: "Locaciones", path: "/locations", icon: "assets/icons/ubicacion.svg" },
  { name: "Usuarios", path: "/users", icon: "assets/icons/avatar.svg" },
  { name: "Equipos de trabajo", path: "/work-groups", icon: "assets/icons/group.svg" },
  { name: "Soporte", path: "/video-call", icon: "assets/icons/engineer.svg" },
  { name: "Calendario", path: "/calendar", icon: "assets/icons/calendario.svg" },
  {
    name: "Catalogos",
    path: "/catalogs",
    icon: "assets/icons/overlay.svg",
    allowRoles: ["663536e2-bdb4-4887-a313-c42d3e00bc37"],
  },
  {
    name: "Catalogos Broker",
    path: "/broker-catalogs",
    icon: "assets/icons/iteration.svg",
    allowRoles: ["663536e2-bdb4-4887-a313-c42d3e00bc37"],
  },
  { name: "Configuración", path: "/config", icon: "assets/icons/settings.svg" },
  { name: "Dashboard", path: "/viewer", icon: "assets/icons/panel.svg", hidden: true },
];

const filters = [
  { name: "Activas", id: "actives" },
  { name: "Inactivas", id: "inactivates" },
  { name: "Todas", id: "all" },
];

export default function MiniDrawer(props) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [filter, setFilter] = React.useState(filters[0]);
  const [ref, bounds] = useMeasure();

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const isMd = useMediaQuery(theme.breakpoints.up("sm"), { defaultMatches: true });
  const history = useHistory();
  const [path, setPath] = React.useState(history.location.pathname);

  const token = useSelector((state) => state.session.token);
  const user = useSelector((state) => state.session.globalUser);
  const institution = useSelector((state) => state.session.institution);

  const [roles, setRoles] = React.useState([]);
  const [hasClientProfile, setHasClientProfile] = React.useState(false);
  const [validateDelete, setValidateDelete] = React.useState(null);
  const [routesFilter, setRoutesFilter] = React.useState([]);
  const [institutions, setInstitutions] = React.useState([]);
  const [showLogin, setShowLogin] = React.useState(!token);
  const isHandoffPath = history.location.pathname === "/auth/handoff";

  const dispatch = useDispatch();

  const rolesService = app.service("users-roles");
  const institutionService = app.service("institutions");

  React.useEffect(() => {
    if (isHandoffPath) {
      setShowLogin(false);
      return history.listen((location) => {
        setPath(location.pathname);
      });
    }

    if (!!token && showLogin) {
      setShowLogin(false);
      history.push("/");
    } else if (!token && !showLogin) {
      setShowLogin(true);
    }

    return history.listen((location) => {
      if (!token && location.pathname !== "/" && location.pathname !== "/auth/handoff") {
        setShowLogin(true);
      } else if (location.pathname !== "/" && !showLogin) {
        setShowLogin(false);
      }
      setPath(location.pathname);
    });
  }, [history, token, isHandoffPath]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const onDisconnect = (e) => {
      console.log("e", e);
    };
    socketApp.on("disconnect", onDisconnect);
    if (!token) {
      return () => {
        socketApp.off("disconnect", onDisconnect);
      };
    }
    socketApp
      .reAuthenticate()
      .then(() => console.log("authenticate"))
      .catch((error) => {
        // No forzar logout por falla de socket: REST puede seguir autenticado.
        console.log("socket reAuthenticate failed", error?.message || error);
      });
    return () => {
      socketApp.off("disconnect", onDisconnect);
    };
  }, [token]);

  const logging = async () => {
    if ("Notification" in window) {
      Notification.requestPermission()
        .then((permission) => {
          if (permission === "granted") {
            firebase
              .messaging()
              .getToken()
              .then((tokenPush) => {
                const service = app.service("usuarios");
                service.patch(user.id, { vc_push: tokenPush }).catch((error) => {
                  console.error("Error updating item:", error);
                });
              })
              .catch((error) => {
                console.log("Error al obtener el token de registro:", error);
              });
          }
        })
        .catch((error) => {
          console.log("Error al solicitar permiso de notificaciones:", error);
        });
    }
  };

  const getInstitutions = async () => {
    setInstitutions([]);
    let params = undefined;

    switch (filter.id) {
      case "actives":
        params = { query: { bl_activo: true } };
        break;
      case "inactivates":
        params = { query: { bl_activo: false } };
        break;
      default:
        params = undefined;
    }

    try {
      const res = await institutionService.find(params);
      setInstitutions(res?.data || []);
    } catch (error) {
      if (error?.name === "NotAuthenticated" || error?.code === 401) {
        setShowLogin(true);
        dispatch({ type: "UNSET_USER", payload: null });
        return;
      }
      console.log("getInstitutions error", error);
    }
  };

  const getRoles = async () => {
    setRoles([]);
    try {
      const userId = user?.id;
      if (!userId) return;
      const res = await rolesService.find({
        query: { $and: [{ id_usuario: userId }] },
      });
      console.log("roles", res?.data);
      setRoles(res?.data || []);
    } catch (error) {
      if (error?.name === "NotAuthenticated" || error?.code === 401) {
        setShowLogin(true);
        dispatch({ type: "UNSET_USER", payload: null });
        return;
      }
      console.log("getRoles error", error);
    }
  };

  React.useEffect(() => {
    if (token) {
      getInstitutions();
      getRoles();
      logging();
    }
  }, [token, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const loadClientProfile = async () => {
      try {
        if (!token || !institution?.id || !user?.id) {
          setHasClientProfile(false);
          return;
        }
        const res = await app.service("clients").find({
          query: {
            id_institucion: institution.id,
            id_usuario: user.id,
            $limit: 1,
            $skip: 0,
          },
        });
        const rows = Array.isArray(res?.data) ? res.data : [];
        setHasClientProfile(rows.length > 0);
      } catch (error) {
        setHasClientProfile(false);
      }
    };
    loadClientProfile();
  }, [token, institution?.id, user?.id]);

  React.useEffect(() => {
    const roleCandidates = [
      ...(roles || []),
      ...(institution?.roles || []),
    ];
    const roleIds = roleCandidates
      .map((r) => r?.rol?.id || r?.id_rol || r?.id || null)
      .filter(Boolean);
    const hasAnyRole = roleIds.length > 0;

    const isClientByRole = roleCandidates.some((r) => {
      const roleName = `${
        r?.rol?.vc_nombre ||
        r?.role?.vc_nombre ||
        r?.vc_nombre ||
        r?.rol_nombre ||
        ""
      }`.toLowerCase();
      const roleInstitution = r?.id_institucion || r?.rol?.id_institucion || null;
      const sameInstitution =
        !institution?.id ||
        !roleInstitution ||
        roleInstitution === institution.id ||
        roleInstitution === "Global";
      return roleName.includes("cliente") && sameInstitution;
    });
    const isClientInInstitution = isClientByRole || hasClientProfile;

    if (!hasAnyRole) {
      setRoutesFilter(routes.filter((e) => e.path === "/mis-productos"));
      return;
    }

    setRoutesFilter(
      routes.filter((e) => {
        if (e.clientOnly && !isClientInInstitution) return false;
        if (e.allowRoles === undefined) return true;
        return e.allowRoles.some((id) => roleIds.includes(id));
      })
    );
  }, [roles, institution, hasClientProfile]);

  const routeSelected =
    routes.find((e) => history.location.pathname === e.path) || routes[0];

  const renderHeaderButton = () => {
    return (
      <Box key="Container Button" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <NotificationComponent key={"Notification Button"} />
        <CatalogsIconButton
          key={"Account Button"}
          model={usuarios}
          icon={"/assets/icons/avatar.svg"}
          item={user}
          color={"white"}
          backgroundColor={"transparent"}
        />
      </Box>
    );
  };

  const isSystem = !!roles.find((e) => e.rol?.vc_nombre === "Sistema");
  const isAdmin = !!roles.find((e) => e.rol?.vc_nombre === "Admin");

  return [
    !institution && (
      <Paper
        key="bg"
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          borderRadius: 0,
          background: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      />
    ),

    (!!institution || history.location.pathname === "/video-call") && (
      <Box key="miniDrawer" sx={{ display: "flex", height: "100%", width: "100%" }}>
        <Helmet>
          <title>{`Lukai | ${path}`}</title>
          <meta name="description" content="Helmet application" />
        </Helmet>

        <CssBaseline />

        {/* Header flotante transparente */}
        <AppBar position="fixed" open={open} elevation={0}>
          <Toolbar
            ref={ref}
            sx={{
              border: "none",
              backgroundColor: "transparent",
              width: "100%",
              minHeight: 64,
            }}
          >
            <IconButton
              key="Drawer Button"
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: 2,
                ...(open && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>

            <Typography variant="h6" noWrap component="div">
              {routeSelected?.name || ""}
            </Typography>

            <Grid
              key="Right Buttons"
              container
              item
              xs="auto"
              position="absolute"
              direction={"row"}
              right={8}
              display={open && !isMd ? "none" : "flex"}
              alignItems="center"
            >
              {renderHeaderButton()}
            </Grid>
          </Toolbar>
        </AppBar>

        {/* Drawer transparente */}
        <Drawer
          variant="permanent"
          open={open}
          onClick={() => {
            if (!isMd) setOpen(false);
          }}
        >
          <DrawerHeader>
            <ListItem
              key={"title"}
              disablePadding
              sx={{
                display: "block",
                position: "absolute",
                left: 0,
                top: 8,
              }}
            >
              <ListItemButton
                disableRipple
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  borderRadius: 2,
                  // nada de background en el contenedor del drawer
                  backgroundColor: "transparent",
                }}
                onClick={() => dispatch(setInstitution(null))}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                    opacity: open ? 1 : 0
                  }}
                >
                  <img
                    src="assets/images/logo192.png"
                    style={{
                      backgroundColor: "white",
                      height: 35,
                      padding: 2,
                      borderRadius: "50%",
                    }}
                    alt=""
                  />
                </ListItemIcon>
                <ListItemText
                  className="text-container"
                  primary={`${institution?.vc_nombre || "Lukai"}`}
                  sx={{ opacity: open ? 1 : 0, overflow: "hidden" }}
                />
              </ListItemButton>
            </ListItem>

            {open && <IconButton onClick={handleDrawerClose}>
              {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>}
          </DrawerHeader>

          {/* Divider transparente (sin borde) */}
          <Divider sx={{ opacity: 0, border: "none" }} />

          <List sx={{ pt: 1 }}>
            {routesFilter
              .filter((e) => !e.hidden)
              .map((route, index) => {
                const selected = route.path === path;
                return (
                  <ListItem
                    key={index}
                    disablePadding
                    sx={{
                      display: "block",
                      backgroundColor: "transparent",
                    }}
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                        borderRadius: 2,
                        color: selected ? theme.palette.primary.main : theme.palette.text.primary,
                        backgroundColor:
                          selected
                            ? (theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.10)"
                              : "rgba(24,39,63,0.10)")
                            : "transparent",
                        "&:hover": {
                          backgroundColor: selected
                            ? (theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.14)"
                              : "rgba(24,39,63,0.14)")
                            : (theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(24,39,63,0.06)"),
                        },
                      }}
                      onClick={() => history.push(route.path)}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        <IconMask
                          color={selected ? theme.palette.primary.main : theme.palette.text.secondary}
                          url={route.icon}
                          size={25}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={route.name}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            <Divider />
            <ThemeButton mode="custom" drawerOpen={open} />
          </List>


        </Drawer>

        {/* Content */}
        <Box
          component="main"
          style={{ paddingTop: bounds.height, width: "100%" }}
          onClick={(e) => {
            if (!isMd) {
              // e.stopPropagation();
              // e.preventDefault();
              setOpen(false);
            }
          }}
          sx={{ flexGrow: 1, p: 0, height: "100%" }}
        >
          {props.children}
        </Box>

        {isMd && (
          <Box p={1} position={"absolute"} bottom={0} right={0}>
            <Typography fontSize={10} style={{ color: theme.palette.text.secondary }}>
              {`Usuario: ${user?.vc_nombre} ${user?.vc_paterno || ""}`}
            </Typography>
            <Typography fontSize={10} style={{ color: theme.palette.text.secondary, textAlign: "right" }}>
              {`Version: ${"4.1.1" || process.env.REACT_APP_VERSION}`}
            </Typography>
          </Box>
        )}
      </Box>
    ),

    <Login
      key={"login-selector"}
      open={showLogin}
      setShowLogin={setShowLogin}
      version={
        <Box
          p={1}
          position={"absolute"}
          bottom={0}
          right={0}
          style={{ zIndex: 9999 }}
        >
          <Typography fontSize={10} style={{ color: theme.palette.text.secondary }}>
            {`Usuario: ${user?.vc_nombre} ${user?.vc_paterno || ""}`}
          </Typography>
          <Typography fontSize={10} style={{ color: theme.palette.text.secondary, textAlign: "right" }}>
            {`Version: ${"4.1.1" || process.env.REACT_APP_VERSION}`}
          </Typography>
        </Box>
      }
    />,

    <Dialog
      key={"institution-selector"}
      open={!showLogin && !institution}
      TransitionComponent={TransitionUp}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        },
      }}
    >
      <DialogTitle>{"Para iniciar selecciona o crea una empresa"}</DialogTitle>
      <DialogContent style={{ height: isMd ? "50vh" : "100%" }}>
        <ButtonGroup fullWidth size="small" style={{ marginBottom: 16 }}>
          {filters.map((item) => (
            <Button
              variant={item.id === filter.id ? "contained" : "outlined"}
              key={item.id}
              onClick={() => setFilter(item)}
            >
              {item.name}
            </Button>
          ))}
        </ButtonGroup>

        {institutions.map((ins) => (
          <ListItemButton
            key={ins.id}
            onClick={() => {
              if (ins.bl_activo) {
                history.push("/");
                dispatch(
                  setInstitution({
                    ...ins,
                    roles: roles.filter(
                      (e) =>
                        e.id_institucion === ins.id ||
                        e.id_institucion === "Global" ||
                        e.id_institucion === null
                    ),
                  })
                );
              }
            }}
          >
            <Box paddingRight={3} style={{ opacity: ins.bl_activo ? 1 : 0.5 }}>
              <IconMask
                color={theme.palette.mode === "dark" ? "white" : "black"}
                url={"/assets/icons/fabrica.svg"}
                size={25}
              />
            </Box>
            <Typography style={{ opacity: ins.bl_activo ? 1 : 0.5 }}>
              {ins.vc_nombre}
            </Typography>

            {(!!isAdmin || !!isSystem) && (
              <ListItemSecondaryAction
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
              >
                {ins.bl_activo ? (
                  <CatalogsIconButton
                    model={instituciones}
                    icon={"/assets/icons/settings.svg"}
                    item={ins}
                    color={theme.palette.text.primary}
                    backgroundColor={"transparent"}
                    size={20}
                    endButtons={
                      <Button
                        {...(!isSystem
                          ? {
                            disabled: true,
                            onMouseEnter: (e) => setAnchorEl(e.currentTarget),
                            onMouseLeave: () => setAnchorEl(null),
                          }
                          : {})}
                        variant="contained"
                        style={{ backgroundColor: "red" }}
                        onClick={() => {
                          setValidateDelete({
                            ...ins,
                            action: () => {
                              institutionService.patch(ins.id, { bl_activo: false });
                            },
                          });
                        }}
                      >
                        <DeleteForeverOutlined />
                      </Button>
                    }
                  />
                ) : (
                  <CatalogsIconButton
                    model={instituciones}
                    icon={"/assets/icons/settings.svg"}
                    item={ins}
                    color={theme.palette.text.primary}
                    backgroundColor={"transparent"}
                    size={20}
                    endButtons={
                      <Button
                        {...(!isSystem
                          ? {
                            disabled: true,
                            onMouseEnter: (e) => setAnchorEl(e.currentTarget),
                            onMouseLeave: () => setAnchorEl(null),
                          }
                          : {})}
                        variant="contained"
                        onClick={() => {
                          setValidateDelete({
                            ...ins,
                            action: () => {
                              institutionService.patch(ins.id, { bl_activo: true });
                            },
                          });
                        }}
                      >
                        {"Activar"}
                      </Button>
                    }
                  />
                )}

                {!isSystem && (
                  <Popover
                    id="mouse-over-popover"
                    sx={{ pointerEvents: "none" }}
                    open={!!anchorEl}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    onClose={() => setAnchorEl(null)}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>
                      Comunicate con TGOne para reactivar esta empresa
                    </Typography>
                  </Popover>
                )}
              </ListItemSecondaryAction>
            )}
          </ListItemButton>
        ))}

        {institutions.length === 0 && <Button>{"!No hay resultados¡"}</Button>}
      </DialogContent>

      <DialogActions>
        <CatalogsIconButton
          key={"catIcon"}
          title={"Crear"}
          mode="title"
          variant="text"
          model={instituciones}
        />
        <Button
          key={"aceptar"}
          onClick={() => {
            dispatch(removeToken());
            dispatch(removeGlobalUser());
            dispatch(removeInstitution());
            setInstitutions([]);
          }}
        >
          {"Cerrar Sesión"}
        </Button>
      </DialogActions>
    </Dialog>,

    <Dialog key={"validate"} open={!!validateDelete} TransitionComponent={Zoom}>
      <DialogTitle>{`Desea eliminar ${validateDelete?.vc_nombre}`}</DialogTitle>
      <DialogContent>
        {`¿Esta acción ${validateDelete?.bl_activo ? "inactivará" : "reactivará"
          } nuevos eventos y registros de sus dispositivos?`}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setValidateDelete(null)}>{"Cancelar"}</Button>
        <Button
          variant="contained"
          color={validateDelete?.bl_activo ? "error" : "primary"}
          onClick={async () => {
            await validateDelete?.action();
            setValidateDelete(null);
            setInstitution([]);
            getInstitutions();
          }}
        >
          {validateDelete?.bl_activo ? "Eliminar" : "Activar"}
        </Button>
      </DialogActions>
    </Dialog>,
  ];
}
